import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { DEFAULT_CATEGORIES, DEFAULT_TASK_CATALOGUE, CATALOGUE_SEED } from '../lib/defaults'
import { ConfirmModal } from './Modals'
import BiCalculator from './BiCalculator'
import '../myitems.css'

const DEFAULT_ITEM_SECTIONS = DEFAULT_CATEGORIES
  .filter(c => c.id !== 'labour' && c.id !== 'additional')
  .map(c => ({ id: c.id, name: c.name, color: c.color, isDefault: true }))
  .concat([{ id: 'additional', name: 'Additional', color: '#e74c3c', isDefault: true }])

const PHASES = ['Workshop preparation', 'Onsite', 'Finish']

const CATVAR = {
  timber:    'var(--c-timber)',
  fixings:   'var(--c-fixings)',
  adhesives: 'var(--c-adhesives)',
  site:      'var(--c-site)',
  additional:'var(--c-additional)',
  labour:    'var(--c-timber)',
}
const catVar = id => CATVAR[id] || 'var(--ink-3)'

const gbp = n => '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const numFmt = n => n == null ? '' : n.toLocaleString('en-GB', { maximumFractionDigits: 3 })

const Icon = {
  search: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></svg>,
  plus:   () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  x:      () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>,
  chev:   () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>,
  grip:   () => <svg width="11" height="16" viewBox="0 0 11 16" fill="currentColor"><circle cx="3" cy="3" r="1.4" /><circle cx="8" cy="3" r="1.4" /><circle cx="3" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="3" cy="13" r="1.4" /><circle cx="8" cy="13" r="1.4" /></svg>,
  calc:   () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="3" width="14" height="18" rx="2" /><line x1="9" y1="7" x2="15" y2="7" /><line x1="9" y1="11" x2="9" y2="11" /><line x1="12" y1="11" x2="12" y2="11" /><line x1="15" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="9" y2="15" /><line x1="12" y1="15" x2="12" y2="15" /></svg>,
}

function NumEdit({ value, onChange, money }) {
  const [foc, setFoc] = useState(false)
  const [draft, setDraft] = useState('')
  const disp = foc ? draft : (value == null ? '' : (money ? value.toFixed(2) : numFmt(value)))
  return (
    <input
      className="ak-edit ak-edit--num"
      inputMode="decimal"
      value={disp}
      placeholder="0"
      onFocus={e => { setFoc(true); setDraft(value == null ? '' : String(value)); requestAnimationFrame(() => e.target.select()) }}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { setFoc(false); const n = parseFloat(draft.replace(/[, ]/g, '')); onChange(isNaN(n) ? null : n) }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(String(value ?? '')); e.target.blur() } }}
    />
  )
}

function TextEdit({ value, onChange, placeholder }) {
  const [draft, setDraft] = useState(value)
  return (
    <input
      className="ak-edit"
      value={draft}
      placeholder={placeholder || ''}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => { if (draft !== value) onChange(draft) }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setDraft(value); e.target.blur() } }}
    />
  )
}

function CatalogueRow({ item, saving, onUpdate, onDelete, onAddBelow }) {
  const total = (item.unit_cost || 0) * (item.default_qty || 0)
  const formatAge = ts => {
    if (!ts) return null
    const days = Math.floor((Date.now() - new Date(ts)) / 86400000)
    if (days === 0) return 'today'
    if (days === 1) return '1d ago'
    if (days < 30) return `${days}d ago`
    return `${Math.floor(days / 30)}mo ago`
  }
  const age = formatAge(item.price_updated_at)
  return (
    <div className="ak-row" style={{ '--cols': '22px minmax(0,1fr) 116px 78px 96px 78px' }}>
      <span className="ak-drag"><Icon.grip /></span>
      <div style={{ minWidth: 0 }}>
        <TextEdit
          value={item.description}
          placeholder="Item description"
          onChange={v => onUpdate(item.id, 'description', v)}
        />
        {age && <div className="ak-cell-sub">Price updated {age}</div>}
      </div>
      <NumEdit
        value={item.unit_cost}
        money
        onChange={v => onUpdate(item.id, 'unit_cost', v ?? 0, { price_updated_at: new Date().toISOString() })}
      />
      <NumEdit
        value={item.default_qty}
        onChange={v => onUpdate(item.id, 'default_qty', v ?? 0)}
      />
      {total > 0
        ? <div className="ak-num ak-num--total tnum">{gbp(total)}</div>
        : <div className="mi-noprice">—</div>}
      <div className="ak-rowact" style={{ gap: 7 }}>
        <button className="mi-addrow" title="Add row below" onClick={() => onAddBelow(item)}><Icon.plus /></button>
        <button className="ak-iconbtn ak-iconbtn--danger" title="Remove" onClick={() => onDelete(item.id)}><Icon.x /></button>
      </div>
      {saving && <div style={{ position: 'absolute', right: 6, top: 6, width: 5, height: 5, borderRadius: '50%', background: 'var(--green)' }} />}
    </div>
  )
}

function LabourLineRow({ item, isPinned, saving, onUpdate, onDelete, onAddBelow }) {
  return (
    <div className="ak-row" style={{ '--cols': '22px minmax(0,1fr) 96px 78px' }}>
      <span className="ak-drag"><Icon.grip /></span>
      <TextEdit
        value={item.description}
        placeholder="e.g. Project admin, Design, Collection…"
        onChange={v => onUpdate(item.id, 'description', v)}
      />
      <div className="mi-days">
        <NumEdit value={item.unit_cost} onChange={v => onUpdate(item.id, 'unit_cost', v ?? 0)} />
      </div>
      <div className="ak-rowact" style={{ gap: 7 }}>
        <button className="mi-addrow" title="Add row below" onClick={() => onAddBelow(item)}><Icon.plus /></button>
        <button
          className="ak-iconbtn ak-iconbtn--danger"
          style={{ visibility: isPinned ? 'hidden' : undefined }}
          onClick={() => onDelete(item.id)}
        ><Icon.x /></button>
      </div>
      {saving && <div style={{ position: 'absolute', right: 6, top: 6, width: 5, height: 5, borderRadius: '50%', background: 'var(--green)' }} />}
    </div>
  )
}

function TaskRow({ task, saving, onUpdate, onDelete }) {
  return (
    <div className="ak-row" style={{ '--cols': '22px minmax(0,1fr) 92px 96px 34px' }}>
      <span className="ak-drag"><Icon.grip /></span>
      <TextEdit
        value={task.name}
        placeholder="Task name"
        onChange={v => onUpdate(task.id, 'name', v)}
      />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div className="mi-mult">
          <button className={task.is_multiplied ? 'on' : ''} onClick={() => onUpdate(task.id, 'is_multiplied', true)}>× qty</button>
          <button className={!task.is_multiplied ? 'on' : ''} onClick={() => onUpdate(task.id, 'is_multiplied', false)}>fixed</button>
        </div>
      </div>
      <div className="mi-days">
        <NumEdit value={task.unit_duration} onChange={v => onUpdate(task.id, 'unit_duration', v ?? 0)} />
      </div>
      <div className="ak-rowact">
        <button className="ak-iconbtn ak-iconbtn--danger" onClick={() => onDelete(task.id)}><Icon.x /></button>
      </div>
      {saving && <div style={{ position: 'absolute', right: 6, top: 6, width: 5, height: 5, borderRadius: '50%', background: 'var(--green)' }} />}
    </div>
  )
}

export default function Catalogue({ session }) {
  const [items, setItems] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [seeded, setSeeded] = useState(false)
  const [activeSection, setActiveSection] = useState('timber')
  const [saving, setSaving] = useState(null)
  const [query, setQuery] = useState('')
  const [addingSection, setAddingSection] = useState(false)
  const [newSectionName, setNewSectionName] = useState('')
  const [sectionOrder, setSectionOrder] = useState(null)
  const [editingSection, setEditingSection] = useState(null)
  const [dragSectionIdx, setDragSectionIdx] = useState(null)
  const [dragOverSectionIdx, setDragOverSectionIdx] = useState(null)
  const [biOpen, setBiOpen] = useState(false)
  const [biCalcOpen, setBiCalcOpen] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)
  const dragRef = useRef(null)

  useEffect(() => { loadCatalogue() }, [])

  const loadCatalogue = async () => {
    const [itemsRes, tasksRes] = await Promise.all([
      supabase.from('item_catalogue').select('*').eq('user_id', session.user.id).order('sort_order'),
      supabase.from('task_catalogue').select('*').eq('user_id', session.user.id).order('sort_order'),
    ])

    let catalogueItems = itemsRes.data || []
    if (catalogueItems.length === 0) {
      const records = CATALOGUE_SEED.map(item => ({ ...item, user_id: session.user.id }))
      const { data } = await supabase.from('item_catalogue').insert(records).select()
      if (data) { catalogueItems = data; setSeeded(true) }
    }
    setItems(catalogueItems)

    let taskItems = tasksRes.data || []
    if (taskItems.length === 0) {
      const taskRecords = DEFAULT_TASK_CATALOGUE.map(t => ({
        user_id: session.user.id, name: t.name, phase: t.phase,
        is_multiplied: t.is_multiplied, unit_duration: t.unit_duration, sort_order: t.sort_order,
      }))
      const { data } = await supabase.from('task_catalogue').insert(taskRecords).select()
      if (data) taskItems = data
    }
    setTasks(taskItems)
    setLoading(false)
  }

  const sections = (() => {
    const base = sectionOrder || DEFAULT_ITEM_SECTIONS
    const knownIds = new Set(base.map(s => s.id))
    const extras = []
    items.forEach(item => {
      if (item.category !== 'labour' && !knownIds.has(item.category)) {
        extras.push({ id: item.category, name: item.category, color: '#8a8579', isDefault: false })
        knownIds.add(item.category)
      }
    })
    return [...base, ...extras]
  })()

  // ── Item ops ─────────────────────────────────────────────────
  const updateItem = async (id, field, value, extra = {}) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: value, ...extra } : i))
    setSaving(id)
    await supabase.from('item_catalogue').update({ [field]: value, ...extra }).eq('id', id)
    setSaving(null)
  }

  const addItem = async (categoryId) => {
    const maxOrder = items.filter(i => i.category === categoryId).length
    const { data } = await supabase.from('item_catalogue').insert({
      user_id: session.user.id, category: categoryId,
      description: '', unit_cost: 0, default_qty: 0,
      sort_order: maxOrder, is_active: true,
    }).select().single()
    if (data) setItems(prev => [...prev, data])
  }

  const addItemBelow = async (afterItem) => {
    const { data } = await supabase.from('item_catalogue').insert({
      user_id: session.user.id, category: afterItem.category,
      description: '', unit_cost: 0, default_qty: 0,
      sort_order: afterItem.sort_order + 1, is_active: true,
    }).select().single()
    if (data) setItems(prev => {
      const copy = [...prev]
      const insertAt = copy.findIndex(i => i.id === afterItem.id) + 1
      copy.splice(insertAt, 0, data)
      return copy
    })
  }

  const deleteItem = async (id) => {
    await supabase.from('item_catalogue').delete().eq('id', id)
    setItems(prev => prev.filter(i => i.id !== id))
  }

  // ── Section ops ───────────────────────────────────────────────
  const addSection = () => {
    const name = newSectionName.trim()
    if (!name) return
    const slug = `custom_${Date.now()}`
    const newSec = { id: slug, name, color: '#8a8579', isDefault: false }
    setSectionOrder(prev => [...(prev || sections), newSec])
    setNewSectionName('')
    setAddingSection(false)
    setActiveSection(slug)
  }

  const renameSection = (sectionId, newName) => {
    if (!newName.trim()) { setEditingSection(null); return }
    setSectionOrder(prev => (prev || sections).map(s =>
      s.id === sectionId ? { ...s, name: newName.trim() } : s
    ))
    setEditingSection(null)
  }

  const deleteSection = async (sectionId) => {
    const sectionItems = items.filter(i => i.category === sectionId)
    if (sectionItems.length > 0) {
      setConfirmModal({
        message: `Delete "${sections.find(s => s.id === sectionId)?.name}" and all its items?`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: async () => {
          await Promise.all(sectionItems.map(i => supabase.from('item_catalogue').delete().eq('id', i.id)))
          setItems(prev => prev.filter(i => i.category !== sectionId))
          setSectionOrder(prev => (prev || sections).filter(s => s.id !== sectionId))
          if (activeSection === sectionId) setActiveSection('timber')
        }
      })
      return
    }
    setSectionOrder(prev => (prev || sections).filter(s => s.id !== sectionId))
    if (activeSection === sectionId) setActiveSection('timber')
  }

  const handleSectionDrop = (e, toIdx) => {
    e.preventDefault()
    const fromIdx = dragRef.current
    if (fromIdx === null || fromIdx === toIdx) { setDragSectionIdx(null); setDragOverSectionIdx(null); return }
    const reordered = [...sections]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    setSectionOrder(reordered)
    setDragSectionIdx(null); setDragOverSectionIdx(null); dragRef.current = null
  }

  // ── Task ops ──────────────────────────────────────────────────
  const updateTask = async (id, field, value) => {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t))
    setSaving(id)
    await supabase.from('task_catalogue').update({ [field]: value }).eq('id', id)
    setSaving(null)
  }

  const addTask = async (phase) => {
    const { data } = await supabase.from('task_catalogue').insert({
      user_id: session.user.id, name: '', phase: phase || 'Onsite',
      is_multiplied: true, unit_duration: 0, sort_order: tasks.length,
    }).select().single()
    if (data) setTasks(prev => [...prev, data])
  }

  const deleteTask = async (id) => {
    await supabase.from('task_catalogue').delete().eq('id', id)
    setTasks(prev => prev.filter(t => t.id !== id))
  }

  const isLabour = activeSection === 'labour'
  const activeSec = sections.find(s => s.id === activeSection)
  const labourItems = items.filter(i => i.category === 'labour').sort((a, b) => a.sort_order - b.sort_order)
  const sectionItems = items.filter(i => i.category === activeSection).sort((a, b) => a.sort_order - b.sort_order)
  const filteredItems = query ? sectionItems.filter(i => i.description.toLowerCase().includes(query.toLowerCase())) : sectionItems

  if (loading) return (
    <div className="ak-app" style={{ textAlign: 'center', paddingTop: '6rem', fontFamily: 'var(--mono)', fontSize: '0.75rem', color: 'var(--ink-3)', letterSpacing: '2px' }}>
      Loading…
    </div>
  )

  const biTasks = tasks.map(t => ({
    id: t.id,
    name: t.name || '',
    section: t.phase || 'Onsite',
    hrs: parseFloat(t.unit_duration) || 0,
    isMultiplied: t.is_multiplied || false,
  }))

  return (
    <>
    <div className="ak-app">
      <ConfirmModal config={confirmModal} onClose={() => setConfirmModal(null)} />

      <div className="ak-page-head" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
        <h1 className="t-page">My Items</h1>
        <span className="ak-page-sub">Commonly used items and prices for your projects</span>
        {seeded && (
          <div style={{
            marginTop: 4, padding: '8px 13px',
            background: 'var(--green-soft)', border: '1px solid var(--green)',
            borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--green-deep)', fontWeight: 500,
          }}>
            Pre-populated with suggested items — update prices and quantities to match how you work.
          </div>
        )}
      </div>

      <div className="ak-md" style={{ gridTemplateColumns: '276px minmax(0,1fr)' }}>

        {/* ── Left rail ── */}
        <div className="ak-md-list">
          <div className="mi-list-head">
            <div className="ak-search" style={{ flex: 1 }}>
              <Icon.search />
              <input
                placeholder="Search library…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          </div>

          {sections.map((sec, idx) => (
            <div
              key={sec.id}
              className={'mi-md-item' + (activeSection === sec.id ? ' active' : '')}
              style={{
                '--cat': catVar(sec.id),
                opacity: dragSectionIdx === idx ? 0.4 : 1,
                outline: dragOverSectionIdx === idx ? '2px solid var(--green)' : undefined,
              }}
              draggable
              onDragStart={e => { e.stopPropagation(); dragRef.current = idx; setDragSectionIdx(idx) }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOverSectionIdx(idx) }}
              onDrop={e => handleSectionDrop(e, idx)}
              onDragEnd={() => { setDragSectionIdx(null); setDragOverSectionIdx(null) }}
              onClick={() => { if (editingSection !== sec.id) setActiveSection(sec.id) }}
            >
              <span className="mi-md-grip"><Icon.grip /></span>
              <span className="ak-dot" style={{ '--cat': catVar(sec.id) }} />
              <div className="mi-md-body">
                {editingSection === sec.id ? (
                  <input
                    className="ak-input"
                    style={{ width: '100%', padding: '2px 6px', fontSize: 13 }}
                    defaultValue={sec.name}
                    autoFocus
                    onClick={e => e.stopPropagation()}
                    onBlur={e => renameSection(sec.id, e.target.value)}
                    onKeyDown={e => {
                      e.stopPropagation()
                      if (e.key === 'Enter') renameSection(sec.id, e.target.value)
                      if (e.key === 'Escape') setEditingSection(null)
                    }}
                  />
                ) : (
                  <div
                    className="mi-md-name"
                    onDoubleClick={e => { e.stopPropagation(); setEditingSection(sec.id) }}
                    title="Double-click to rename"
                  >{sec.name}</div>
                )}
                <div className="mi-md-count" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {items.filter(i => i.category === sec.id).length} items
                  {!sec.isDefault && (
                    <button
                      style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1, marginLeft: 2 }}
                      onClick={e => { e.stopPropagation(); deleteSection(sec.id) }}
                      title="Delete section"
                    >×</button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Labour — fixed at end, not draggable */}
          <div
            className={'mi-md-item' + (isLabour ? ' active' : '')}
            style={{ '--cat': 'var(--c-timber)' }}
            onClick={() => setActiveSection('labour')}
          >
            <span className="mi-md-grip" style={{ opacity: 0 }}><Icon.grip /></span>
            <span className="ak-dot" style={{ '--cat': 'var(--c-timber)' }} />
            <div className="mi-md-body">
              <div className="mi-md-name">Labour &amp; B&amp;I</div>
              <div className="mi-md-count">{labourItems.length} lines · {tasks.length} tasks</div>
            </div>
          </div>

          {addingSection ? (
            <div style={{ display: 'flex', gap: 6, padding: '6px 4px', alignItems: 'center' }}>
              <input
                className="ak-input"
                style={{ flex: 1 }}
                value={newSectionName}
                autoFocus
                placeholder="Section name…"
                onChange={e => setNewSectionName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') addSection()
                  if (e.key === 'Escape') { setAddingSection(false); setNewSectionName('') }
                }}
              />
              <button className="ak-btn ak-btn--primary ak-btn--sm" onClick={addSection}>Add</button>
              <button className="ak-btn ak-btn--ghost ak-btn--sm" onClick={() => { setAddingSection(false); setNewSectionName('') }}>×</button>
            </div>
          ) : (
            <button
              className="ak-add"
              style={{ width: '100%', justifyContent: 'center', marginTop: 8, borderRadius: 'var(--radius-ctl)' }}
              onClick={() => setAddingSection(true)}
            >
              <Icon.plus /> Add section
            </button>
          )}
        </div>

        {/* ── Detail panel ── */}
        <div style={{ background: 'var(--surface)', minHeight: 400 }}>

          {!isLabour && activeSec && (
            <>
              <div className="mi-detail-head" style={{ '--cat': catVar(activeSection) }}>
                <span className="ak-dot" style={{ '--cat': catVar(activeSection) }} />
                <span className="ttl">{activeSec.name}</span>
                <span className="ak-badge">{sectionItems.length}</span>
              </div>
              <div className="ak-table-head" style={{ '--cols': '22px minmax(0,1fr) 116px 78px 96px 78px' }}>
                <span /><span>Item</span><span className="r">Unit cost</span><span className="r">Qty</span><span className="r">Total</span><span />
              </div>
              {filteredItems.length === 0 && (
                <div style={{ padding: '2rem', textAlign: 'center', fontSize: 13, color: 'var(--ink-3)' }}>
                  {query ? 'No items match your search' : 'No items yet — add one below'}
                </div>
              )}
              {filteredItems.map(item => (
                <CatalogueRow
                  key={item.id}
                  item={item}
                  saving={saving === item.id}
                  onUpdate={updateItem}
                  onDelete={deleteItem}
                  onAddBelow={addItemBelow}
                />
              ))}
              <div style={{ padding: '12px 16px 16px' }}>
                <button className="ak-add" onClick={() => addItem(activeSection)}><Icon.plus /> Add item</button>
              </div>
            </>
          )}

          {isLabour && (
            <>
              <div className="mi-band mi-band--labour">
                <span className="ttl">Labour lines</span>
                <span className="hint">Admin, design, delivery, waste disposal</span>
              </div>
              <div className="ak-table-head" style={{ '--cols': '22px minmax(0,1fr) 96px 78px' }}>
                <span /><span>Line item</span><span className="r">Default days</span><span />
              </div>
              {labourItems.length === 0 && (
                <div style={{ padding: '2rem', textAlign: 'center', fontSize: 13, color: 'var(--ink-3)' }}>
                  No labour lines yet — add one below
                </div>
              )}
              {labourItems.map((item, idx) => (
                <LabourLineRow
                  key={item.id}
                  item={item}
                  isPinned={idx === 0}
                  saving={saving === item.id}
                  onUpdate={updateItem}
                  onDelete={deleteItem}
                  onAddBelow={addItemBelow}
                />
              ))}
              <div style={{ padding: '12px 16px 16px' }}>
                <button className="ak-add" onClick={() => addItem('labour')}><Icon.plus /> Add line</button>
              </div>

              <div className={'mi-band' + (biOpen ? '' : ' collapsed')} onClick={() => setBiOpen(o => !o)}>
                <span className="ic"><Icon.calc /></span>
                <span className="ttl">Build &amp; Install tasks</span>
                <span className="hint">Used in the B&amp;I Calculator per project</span>
                <span className="chev"><Icon.chev /></span>
              </div>

              {biOpen && (
                <div style={{ padding: '12px 16px' }}>
                  <button
                    className="ak-btn ak-btn--primary ak-btn--sm"
                    onClick={e => { e.stopPropagation(); setBiOpen(false); setBiCalcOpen(true) }}
                  >
                    <Icon.calc /> Open B&amp;I Calculator
                  </button>
                  <p style={{ marginTop: 8, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', lineHeight: 1.6 }}>
                    {tasks.length} task{tasks.length !== 1 ? 's' : ''} defined · Edit in the calculator to update phases, durations and multipliers.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {biCalcOpen && (
      <BiCalculator
        context="library"
        sections={PHASES}
        tasks={biTasks}
        subtitle="My Items — Task Library"
        onTaskName={(id, name, persist) => {
          setTasks(prev => prev.map(t => t.id === id ? { ...t, name } : t))
          if (persist) supabase.from('task_catalogue').update({ name }).eq('id', id)
        }}
        onTaskHrs={(id, hrs) => {
          setTasks(prev => prev.map(t => t.id === id ? { ...t, unit_duration: hrs } : t))
          supabase.from('task_catalogue').update({ unit_duration: hrs }).eq('id', id)
        }}
        onTaskMultiplied={(id, val) => {
          setTasks(prev => prev.map(t => t.id === id ? { ...t, is_multiplied: val } : t))
          supabase.from('task_catalogue').update({ is_multiplied: val }).eq('id', id)
        }}
        onTaskDelete={deleteTask}
        onTaskAdd={(phase) => addTask(phase)}
        onReorderTasks={async (reordered) => {
          const updated = reordered.map(nt => {
            const orig = tasks.find(t => t.id === nt.id)
            return orig ? { ...orig, phase: nt.section } : null
          }).filter(Boolean)
          setTasks(updated)
          await Promise.all(updated.map((t, i) =>
            supabase.from('task_catalogue').update({ sort_order: i, phase: t.phase }).eq('id', t.id)
          ))
        }}
        onSectionAdd={() => {}}
        onSectionDelete={() => {}}
        onSectionRename={() => {}}
        onReorderSections={() => {}}
        onClose={() => setBiCalcOpen(false)}
      />
    )}
    </>
  )
}
